import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { getAdminQuestions } from "../../functions/getAdminQuestions";
import { ensureThemeJsonArray, isTrue } from "../../../functions/themeAssets";
import { GuidedTourOverlay, useGuidedTour } from "../../components/GuidedTour";

const TOUR_STORAGE_KEY = "adminRulesTour_seen";
// Read by editor.jsx: set when the rules-page tour hands off there.
const TOUR_HANDOFF_KEY = "adminRulesEditorTour_handoff";

const TOUR_STEPS = [
  {
    selector: ".rules-php-card--left",
    title: "Game Content",
    description:
      "Set the browser tab name, the title shown on the game screen, the thank-you page text, and the custom rules players see before they start.",
  },
  {
    selector: ".themeupdate-rules-repeater",
    title: "Custom Rules",
    description: "Add up to 6 short rules explaining how to play — shown to players before the game starts.",
  },
  {
    selector: ".rules-php-card--right",
    title: "Game Settings",
    description: "Configure scoring, question count, popups and colors for this theme from here.",
  },
  {
    selector: "#leaderboard-toggle-row",
    title: "Leaderboard",
    description:
      "Turn this on to show the top 5 scores (plus the player's own rank if they're not in the top 5) on the thank-you page instead of just their score.",
  },
  {
    selector: "#no-of-questions",
    title: "Questions to Display",
    description: "How many questions from your question bank actually get used in the game. You'll build that question bank next.",
  },
  {
    selector: ".rules-action-holder",
    title: "Save & Continue",
    description:
      "\"Next\" saves everything here and takes you to the Question Editor, where you build the question bank for this theme. Click Next to see it now — the tour will pick up right where it left off.",
  },
];

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_TITLE_LEN = 150;
const MAX_TAB_NAME_LEN = 80;
const MAX_THANKYOU_LEN = 300;
const MAX_POPUP_LEN = 100;
const MAX_WRONG_POINTS = 10;

/** <input type="color"> only accepts #rrggbb */
function toColorInputValue(val, fallback) {
  const v = String(val || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  return fallback;
}

const toInt = (v, fallback) => {
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) ? fallback : n;
};

/** Normalizes a saved colour rotation into exactly 3 valid `#rrggbb` values. */
function toColorTriple(val, fallback) {
  const arr = Array.isArray(val) ? val : [];
  return [0, 1, 2].map((i) => toColorInputValue(arr[i], fallback));
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [saving, setSaving] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const [tabName, setTabName] = useState("");
  const [title, setTitle] = useState("");
  const [thankYou, setThankYou] = useState("");
  const [rules, setRules] = useState([""]);
  const [correctPoints, setCorrectPoints] = useState("10");
  const [wrongPoints, setWrongPoints] = useState("0");
  const [shuffle, setShuffle] = useState(true);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(false);
  const [noOfQuestions, setNoOfQuestions] = useState("10");
  const [correctPopup, setCorrectPopup] = useState("");
  const [wrongPopup, setWrongPopup] = useState("");
  const [textColor, setTextColor] = useState("#ffffff");
  const [boxColor, setBoxColor] = useState("#0000ff");
  const [answerColor, setAnswerColor] = useState("#0000ff");
  const [questionColors, setQuestionColors] = useState(["#0000ff", "#0000ff", "#0000ff"]);
  const [selectedColors, setSelectedColors] = useState(["#0000ff", "#0000ff", "#0000ff"]);

  const { tourRunning, tourStep, tourRect, startTour, endTour, setTourStep } = useGuidedTour({
    steps: TOUR_STEPS,
    autoStartKey: TOUR_STORAGE_KEY,
  });
  const isLastTourStep = tourStep >= TOUR_STEPS.length - 1;
  const handleTourNext = () => {
    if (isLastTourStep) {
      try {
        sessionStorage.setItem(TOUR_HANDOFF_KEY, "1");
      } catch (_) {
        /* ignore */
      }
      endTour();
      navigate("/admin/editor");
      return;
    }
    setTourStep((i) => i + 1);
  };

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  // how many questions exist (the game cannot ask for more than that)
  useEffect(() => {
    if (!currentTheme || !adminToken) return;
    getAdminQuestions({ token: adminToken, themeName: currentTheme })
      .then((json) => setTotalQuestions((json.questions || []).length))
      .catch(() => setTotalQuestions(0));
  }, [currentTheme, adminToken]);

  useEffect(() => {
    if (!data) return;
    const r = ensureThemeJsonArray(data.rules);
    setRules(r.length ? r.map(String) : [""]);
    setTabName(String(data.tab_name ?? "Save Them"));
    setTitle(String(data.landing_page_title ?? ""));
    setThankYou(String(data.custom_text_thank_you_page ?? ""));
    setCorrectPoints(String(toInt(data.per_question_points, 10)));
    setWrongPoints(String(toInt(data.wrong_question_points, 0)));
    setShuffle(isTrue(data.shuffle_questions));
    setLeaderboardEnabled(isTrue(data.leaderboard_toggle));
    setNoOfQuestions(String(toInt(data.no_of_questions, 10)));
    setCorrectPopup(String(data.correct_popup ?? ""));
    setWrongPopup(String(data.wrong_popup ?? ""));
    setTextColor(toColorInputValue(data.landing_page_title_color, "#ffffff"));
    const nextBoxColor = toColorInputValue(data.questionbox_color, "#0000ff");
    const nextAnswerColor = toColorInputValue(data.answer_color, "#0000ff");
    setBoxColor(nextBoxColor);
    setAnswerColor(nextAnswerColor);
    setQuestionColors(toColorTriple(data.question_colors, nextBoxColor));
    setSelectedColors(toColorTriple(data.selected_colors, nextAnswerColor));
  }, [data]);

  const handleRuleChange = (idx, val) => {
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleQuestionColorChange = (idx, val) => {
    const arr = [...questionColors];
    arr[idx] = val;
    setQuestionColors(arr);
  };

  const handleSelectedColorChange = (idx, val) => {
    const arr = [...selectedColors];
    arr[idx] = val;
    setSelectedColors(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Up to ${MAX_RULES} rules.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const trimmedRules = rules.map((r) => r.trim()).filter(Boolean);
    if (trimmedRules.length === 0) return "At least one rule is required.";
    if (trimmedRules.some((r) => r.length > MAX_RULE_LEN)) return `Each rule must be ${MAX_RULE_LEN} characters or less.`;
    if (!tabName.trim()) return "Tab name cannot be empty.";
    if (tabName.length > MAX_TAB_NAME_LEN) return `The tab name can be at most ${MAX_TAB_NAME_LEN} characters.`;
    if (title.length > MAX_TITLE_LEN) return `The title can be at most ${MAX_TITLE_LEN} characters.`;
    if (thankYou.length > MAX_THANKYOU_LEN) return `The thank you text can be at most ${MAX_THANKYOU_LEN} characters.`;

    if (!correctPopup.trim()) return "Correct answer popup text cannot be empty.";
    if (correctPopup.length > MAX_POPUP_LEN) return `Correct answer popup can be at most ${MAX_POPUP_LEN} characters.`;
    if (!wrongPopup.trim()) return "Wrong answer popup text cannot be empty.";
    if (wrongPopup.length > MAX_POPUP_LEN) return `Wrong answer popup can be at most ${MAX_POPUP_LEN} characters.`;

    const correct = toInt(correctPoints, NaN);
    const wrong = toInt(wrongPoints, NaN);
    const count = toInt(noOfQuestions, NaN);
    if (Number.isNaN(correct) || correct < 0) return "Correct answer points cannot be less than 0.";
    if (Number.isNaN(wrong) || wrong < 0 || wrong > MAX_WRONG_POINTS) {
      return `Incorrect answer points must be between 0 and ${MAX_WRONG_POINTS}.`;
    }
    if (Number.isNaN(count) || count < 1) return "Number of questions must be at least 1.";
    if (count > totalQuestions) return "Display question count exceeds available questions.";
    return null;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      Swal.fire("Error", problem, "error");
      return;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            rules: rules.map((r) => r.trim()).filter(Boolean),
            tab_name: tabName.trim(),
            landing_page_title: title,
            custom_text_thank_you_page: thankYou,
            landing_page_title_color: textColor,
            questionbox_color: boxColor,
            answer_color: answerColor,
            question_colors: questionColors,
            selected_colors: selectedColors,
            shuffle_questions: shuffle,
            per_question_points: toInt(correctPoints, 10),
            wrong_question_points: toInt(wrongPoints, 0),
            no_of_questions: toInt(noOfQuestions, 10),
            leaderboard_toggle: leaderboardEnabled,
            correct_popup: correctPopup,
            wrong_popup: wrongPopup,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
      navigate("/admin/editor");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="themeupdate-panel-wide" style={{ marginBottom: 20 }}>
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">
            <span>Game Rules &amp; Settings</span>
            <button type="button" className="themeupdate-tour-btn" onClick={startTour}>
              <i className="fa-solid fa-circle-info" /> Take a Tour
            </button>
          </div>
        </div>
      </div>

      <div className="themeupdate-row rules-php-layout">
        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--left">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Tab Name:</h4>
              <input
                type="text"
                className="themeupdate-input"
                maxLength={MAX_TAB_NAME_LEN}
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
              />
              <p className="rules-note">
                * This name is shown on the browser tab. * Maximum character limit should be {MAX_TAB_NAME_LEN} characters.
              </p>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 16 }}>
                Add Custom Title:
              </h4>
              <textarea
                className="themeupdate-input rules-php-textarea"
                maxLength={MAX_TITLE_LEN}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <p className="rules-note">
                * This title will reflect on the main game page. * Maximum character limit should be {MAX_TITLE_LEN}{" "}
                characters.
              </p>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 16 }}>
                Custom Text Thank You Page:
              </h4>
              <textarea
                className="themeupdate-input rules-php-textarea"
                maxLength={MAX_THANKYOU_LEN}
                value={thankYou}
                onChange={(e) => setThankYou(e.target.value)}
              />
              <p className="rules-note">
                * This text will reflect on the thank you page. * Maximum character limit should be {MAX_THANKYOU_LEN}{" "}
                characters.
              </p>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 16 }}>
                Add Custom Rules
              </h4>
              <div className="themeupdate-rules-repeater">
                {rules.map((rule, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Rules"
                      maxLength={MAX_RULE_LEN}
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveRule(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add new rule
                </button>
                <p className="rules-note">
                  <code>
                    * Up to {MAX_RULES} rules, at least 1 required, {MAX_RULE_LEN} characters each
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--right">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Page Content</h4>

              <div className="rules-php-marks-row">
                <label htmlFor="correct-points" className="rules-php-marks-label">
                  Correct Answer Points
                </label>
                <input
                  id="correct-points"
                  type="number"
                  min="0"
                  step="1"
                  className="themeupdate-input rules-php-number"
                  value={correctPoints}
                  onChange={(e) => setCorrectPoints(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="wrong-points" className="rules-php-marks-label">
                  Incorrect Answer Points (0 - {MAX_WRONG_POINTS})
                </label>
                <input
                  id="wrong-points"
                  type="number"
                  min="0"
                  max={MAX_WRONG_POINTS}
                  step="1"
                  className="themeupdate-input rules-php-number"
                  value={wrongPoints}
                  onChange={(e) => setWrongPoints(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row">
                <label className="rules-php-marks-label">Shuffle Questions</label>
                <Switch checked={shuffle} onChange={setShuffle} onColor="#4caf50" />
              </div>

              <div className="rules-php-marks-row" id="leaderboard-toggle-row">
                <label className="rules-php-marks-label">Show Leaderboard</label>
                <Switch checked={leaderboardEnabled} onChange={setLeaderboardEnabled} onColor="#4caf50" />
              </div>
              <p className="rules-note">
                * When on, the thank you page shows the top 5 scores (plus the player's own rank if they're not in
                the top 5) instead of their score.
              </p>

              <div className="rules-php-marks-row">
                <label htmlFor="no-of-questions" className="rules-php-marks-label">
                  No of questions to display (available: {totalQuestions})
                </label>
                <input
                  id="no-of-questions"
                  type="number"
                  min="1"
                  max={totalQuestions || undefined}
                  step="1"
                  className="themeupdate-input rules-php-number"
                  value={noOfQuestions}
                  onChange={(e) => setNoOfQuestions(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="correct-popup" className="rules-php-marks-label">
                  Correct answer popup
                </label>
                <textarea
                  id="correct-popup"
                  maxLength={MAX_POPUP_LEN}
                  className="themeupdate-input rules-php-textarea"
                  value={correctPopup}
                  onChange={(e) => setCorrectPopup(e.target.value)}
                />
                <p className="rules-note">* Maximum {MAX_POPUP_LEN} characters.</p>
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="wrong-popup" className="rules-php-marks-label">
                  Wrong answer popup
                </label>
                <textarea
                  id="wrong-popup"
                  maxLength={MAX_POPUP_LEN}
                  className="themeupdate-input rules-php-textarea"
                  value={wrongPopup}
                  onChange={(e) => setWrongPopup(e.target.value)}
                />
                <p className="rules-note">* Maximum {MAX_POPUP_LEN} characters.</p>
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="text-color" className="rules-php-marks-label">
                  Text Color
                </label>
                <input
                  id="text-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="box-color" className="rules-php-marks-label">
                  Box Color
                </label>
                <input
                  id="box-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={boxColor}
                  onChange={(e) => setBoxColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="answer-color" className="rules-php-marks-label">
                  Selected Box Color
                </label>
                <input
                  id="answer-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={answerColor}
                  onChange={(e) => setAnswerColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label className="rules-php-marks-label">
                  Question Color (rotates every 3 questions)
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {questionColors.map((c, i) => (
                    <input
                      key={i}
                      type="color"
                      title={`Question Color ${i + 1}`}
                      className="themeupdate-color-input rules-php-color-input"
                      value={c}
                      onChange={(e) => handleQuestionColorChange(i, e.target.value)}
                    />
                  ))}
                </div>
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label className="rules-php-marks-label">
                  Selected Color (rotates every 3 questions)
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {selectedColors.map((c, i) => (
                    <input
                      key={i}
                      type="color"
                      title={`Selected Color ${i + 1}`}
                      className="themeupdate-color-input rules-php-color-input"
                      value={c}
                      onChange={(e) => handleSelectedColorChange(i, e.target.value)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rules-action-holder">
        <button type="button" className="save-and-continue" onClick={handleSave} disabled={saving}>
          Next
        </button>
      </div>

      <GuidedTourOverlay
        steps={TOUR_STEPS}
        tourRunning={tourRunning}
        tourStep={tourStep}
        tourRect={tourRect}
        onSkip={endTour}
        onNext={handleTourNext}
        nextLabel={isLastTourStep ? "Continue in Question Editor →" : undefined}
      />
    </>
  );
};

export default Rules;
